import React, { useEffect, useState } from 'react';
import {
  getRecentIncidents,
  getMonthlySavings,
  getMonthlySavingsDates,
  getEventsList,
  snoozeAlert,
} from 'parity-network-requests';
import { useParams, useHistory } from 'react-router-dom';
import Button from 'antd/lib/button';
import ConfigProvider from 'antd/lib/config-provider';
import Dropdown from 'antd/lib/dropdown';
import Empty from 'antd/lib/empty';
import Menu from 'antd/lib/menu';
import notification from 'antd/lib/notification';
import Radio from 'antd/lib/radio';
import Spin from 'antd/lib/spin';
import Table from 'antd/lib/table';
import moment from 'moment';
import { MonthlyCard } from '../MonthlyCard';
import { CustomSnoozeModal } from '../CustomSnoozeModal';
import { config } from '../../config';

// ICONS
import snoozeIconBlue from '../../icons/snooze-blue.svg';
import snoozeDropdownArrow from '../../icons/arrow-down-blue.svg';
import { CheckmarkIcon } from './components/CheckmarkIcon';
import { SnoozeIcon } from './components/SnoozeIcon';

// IMAGES
import noAlerts from '../../images/no-alerts.png';
import alertsNotSetupLight from '../../images/alerts-not-setup-light.png';
import alertsNotSetupDark from '../../images/alerts-not-setup-dark.png';
import noSnoozedAlertsLight from '../../images/no-snoozed-alerts-light.png';
import noSnoozedAlertsDark from '../../images/no-snoozed-alerts-dark.png';
import environmentalImpactIconLight from '../../images/environmental-impact-light.png';
import environmentalImpactIconDark from '../../images/environmental-impact-dark.png';
import waitingIcon from '../../images/waiting.png';
import waitingOnIcon from '../../images/waiting-contract.png';

import './SingleBuildingHome.less';

const { REACT_APP_BUILD_ENV } = config;

export const SingleBuildingHome = (props) => {
  const [monthlyData, setMonthlyData] = useState({});
  const [monthlyDates, setMonthlyDates] = useState([]);
  const [selectedMonthlyDate, setSelectedMonthlyDate] = useState('');
  const [alertsSetup, setAlertsSetup] = useState(false);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [alertsCategory, setAlertsCategory] = useState('New');
  const [nonSnoozedAlerts, setNonSnoozedAlerts] = useState([]);
  const [snoozedAlerts, setSnoozedAlerts] = useState([]);
  const [snoozedAlert, setSnoozedAlert] = useState({});
  const [isCustomSnoozeModalOpen, setIsCustomSnoozeModalOpen] = useState(false);
  const [greeting, setGreeting] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { buildingName, buildings, theme } = props;
  const { building } = useParams();
  const history = useHistory();

  // This function is called to set a building in the session if going to the app using a link
  const storeBuildingInSession = () => {
    const selectedBuildingFromSession = sessionStorage.getItem('selectedBuilding');
    if (!selectedBuildingFromSession) sessionStorage.setItem('selectedBuilding', building);
  };

  const getBuildingPermissions = () => {
    const selectedBuildingFromSession = sessionStorage.getItem('selectedBuilding');
    const selectedBuildingObj = buildings.find((buildingObj) => buildingObj.identifier === selectedBuildingFromSession);
    setPermissions(selectedBuildingObj.permissions);
    return selectedBuildingObj.permissions;
  };

  const getGreeting = () => {
    let greetingStr = '';
    const hour = moment().hour();

    if (hour < 12) {
      greetingStr = 'Good morning';
    } else if (hour >= 12 && hour < 18) {
      greetingStr = 'Good afternoon';
    } else {
      greetingStr = 'Good evening';
    }

    setGreeting(greetingStr);
  };

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);

      const buildingPermissions = getBuildingPermissions();

      if (buildingPermissions.indexOf('savings') !== -1) {
        const monthlySavings = await getMonthlySavings(REACT_APP_BUILD_ENV, building, '');
        setMonthlyData(monthlySavings);
        const monthlyDatesList = await getMonthlySavingsDates(REACT_APP_BUILD_ENV, building);
        setMonthlyDates(monthlyDatesList);
        setSelectedMonthlyDate(monthlyDatesList[0]);
      }

      if (buildingPermissions.indexOf('alerts') !== -1) {
        const incidentsList = await getRecentIncidents(REACT_APP_BUILD_ENV, building);
        setAlertsSetup(incidentsList.alerts_setup);

        const activeEventsFromApi = await getEventsList(REACT_APP_BUILD_ENV, building, { is_resolved: false });

        const sortedNonSnoozedActiveEvents = activeEventsFromApi.results.filter(
          (event) => event.snooze_context.is_snoozed === false,
        );
        const sortedSnoozedActiveEvents = activeEventsFromApi.results.filter(
          (event) => event.snooze_context.is_snoozed === true,
        );

        setActiveAlerts(activeEventsFromApi.results);
        setNonSnoozedAlerts(sortedNonSnoozedActiveEvents);
        setSnoozedAlerts(sortedSnoozedActiveEvents);
      }

      setIsLoading(false);
    }

    storeBuildingInSession();
    fetchData();
    getGreeting();
    window.document.title = `${buildingName} | Home`;
  }, [building, buildingName]);

  const user = JSON.parse(localStorage.getItem('current_user'));
  const { first_name: firstName } = user;

  const updateMonthlyDate = async (value) => {
    const monthlySavings = await getMonthlySavings(REACT_APP_BUILD_ENV, building, value.date);
    setMonthlyData(monthlySavings);
    setSelectedMonthlyDate(value);
  };

  const onSnoozeButtonPress = async (alert) => {
    const { alert_uid_hash } = alert;
    let response;

    if (alert.snooze_context.is_snoozed) {
      response = await snoozeAlert(REACT_APP_BUILD_ENV, alert_uid_hash, 0);

      if (response === true) {
        // Handle success
        notification.info({
          className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
          placement: 'topRight',
          duration: 5,
          icon: <CheckmarkIcon theme={theme} />,
          message: `This alert has been unsnoozed`,
          description: `You will resume receiving notifications.`,
        });
      } else {
        notification.info({
          className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
          placement: 'topRight',
          duration: 5,
          icon: <CheckmarkIcon theme={theme} />,
          message: `This alert could not be unsnoozed`,
          description: `We encountered an error while trying to unsnooze this alert. Please try again.`,
        });
      }
    } else {
      response = await snoozeAlert(REACT_APP_BUILD_ENV, alert_uid_hash, 3);

      if (response === true) {
        // Handle success
        notification.info({
          className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
          placement: 'topRight',
          duration: 5,
          icon: <CheckmarkIcon theme={theme} />,
          message: `This alert has been snoozed`,
          description: `You will resume receiving notifications when you unsnooze it.`,
        });
      } else {
        notification.info({
          className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
          placement: 'topRight',
          duration: 5,
          icon: <CheckmarkIcon theme={theme} />,
          message: `This alert could not be snoozed`,
          description: `We encountered an error while trying to snooze this alert. Please try again.`,
        });
      }
    }

    const activeEventsFromApi = await getEventsList(REACT_APP_BUILD_ENV, building, { is_resolved: false });

    const nonSnoozedActiveEventsFromApi = activeEventsFromApi.results.filter(
      (event) => event.snooze_context.is_snoozed === false,
    );
    const snoozedActiveEventsFromApi = activeEventsFromApi.results.filter(
      (event) => event.snooze_context.is_snoozed === true,
    );

    setActiveAlerts(activeEventsFromApi.results);
    setNonSnoozedAlerts(nonSnoozedActiveEventsFromApi);
    setSnoozedAlerts(snoozedActiveEventsFromApi);
  };

  const submitSnoozeAlert = async (hours) => {
    const { alert_uid_hash } = snoozedAlert;

    setIsCustomSnoozeModalOpen(false);
    const response = await snoozeAlert(REACT_APP_BUILD_ENV, alert_uid_hash, hours);

    if (response === true) {
      // Handle success
      notification.info({
        className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
        placement: 'topRight',
        duration: 5,
        icon: <CheckmarkIcon theme={theme} />,
        message: `This alert has been snoozed`,
        description: `You will resume receiving notifications from it starting ${moment()
          .add(hours, 'hours')
          .format('LLL')}`,
      });
    } else {
      notification.info({
        className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
        placement: 'topRight',
        duration: 5,
        icon: <CheckmarkIcon theme={theme} />,
        message: `This alert could not be snoozed`,
        description: `We encountered an error while trying to snooze this alert. Please try again.`,
      });
    }

    const activeEventsFromApi = await getEventsList(REACT_APP_BUILD_ENV, building, { is_resolved: false });

    const nonSnoozedActiveEventsFromApi = activeEventsFromApi.results.filter(
      (event) => event.snooze_context.is_snoozed === false,
    );
    const snoozedActiveEventsFromApi = activeEventsFromApi.results.filter(
      (event) => event.snooze_context.is_snoozed === true,
    );

    setActiveAlerts(activeEventsFromApi.results);
    setNonSnoozedAlerts(nonSnoozedActiveEventsFromApi);
    setSnoozedAlerts(snoozedActiveEventsFromApi);
  };

  const getAlertSeverityColour = (alert) => {
    if (alert.severity === 'high') return '#EA856F';
    if (alert.severity === 'medium') return '#DEC802';
    return '#796FEA';
  };

  const paginationProperties = {
    className: 'building-home-alerts-pagination',
    hideOnSinglePage: true,
    pageSize: 3,
    showSizeChanger: false,
    total: activeAlerts.length,
    showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} alerts`,
  };

  const renderNoNonSnoozedAlerts = () => {
    return (
      <Empty
        className="building-home-no-active-alerts-container"
        image={noAlerts}
        description={
          <div className="building-home-no-active-alerts-content">
            <strong>Good News! Nothing needs your attention right now</strong>
            <span>We will alert you when there are problems happening with your building</span>
          </div>
        }
      />
    );
  };

  const renderNoSnoozedAlerts = () => {
    return (
      <Empty
        className="building-home-no-snoozed-alerts-container"
        image={theme === 'light' ? noSnoozedAlertsLight : noSnoozedAlertsDark}
        description={
          <div className="building-home-no-snoozed-alerts-content">
            <strong>Alerts that have been snoozed will appear here</strong>
            <span>You can snooze your alerts when you want to temporarily pause e-mail and text notifications</span>
          </div>
        }
      />
    );
  };

  const snoozeMenu = (
    <Menu className={`alerts-snooze-menu app-dropdown-menu app-dropdown-menu-${theme}`}>
      <Menu.Item key="0" disabled>
        <div role="presentation">Snooze for...</div>
      </Menu.Item>
      <Menu.Item key="1">
        <div role="presentation" onClick={() => submitSnoozeAlert(3)}>
          3 hours
        </div>
      </Menu.Item>
      <Menu.Item key="2">
        <div role="presentation" onClick={() => submitSnoozeAlert(6)}>
          6 hours
        </div>
      </Menu.Item>
      <Menu.Item key="3">
        <div role="presentation" onClick={() => submitSnoozeAlert(12)}>
          12 hours
        </div>
      </Menu.Item>
      <Menu.Item key="4">
        <div role="presentation" onClick={() => submitSnoozeAlert(24)}>
          24 hours
        </div>
      </Menu.Item>
      <Menu.Item key="5">
        <div role="presentation" onClick={() => setIsCustomSnoozeModalOpen(true)}>
          Custom
        </div>
      </Menu.Item>
    </Menu>
  );

  const activeAlertsTableColumns = [
    {
      title: 'ALERTS',
      dataIndex: ['name', 'severity'],
      render: (text, alert) => {
        const incidentId = alert.incident_uid_hash;
        const eventId = alert.uid_hash;
        const severity = alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1);
        return (
          <div className="building-home-alert-container">
            <div className="building-home-alert-colour" style={{ borderLeftColor: getAlertSeverityColour(alert) }} />
            <div className="building-home-alert-contents">
              {alertsCategory === 'Snoozed' && (
                <div className="building-home-alert-snoozed-info">
                  <SnoozeIcon theme={theme} />
                  <span className="building-home-alert-snoozed-until">
                    {`Snoozed until ${moment(alert.snooze_context.snoozed_until).format('MMM D, YYYY (LT)')}`}
                  </span>
                </div>
              )}
              <span
                className="building-home-alert-title"
                onClick={() => history.push(`/alerts/${building}?incident_id=${incidentId}&event_id=${eventId}`)}
                onKeyDown={() => history.push(`/alerts/${building}?incident_id=${incidentId}&event_id=${eventId}`)}
                role="link"
                tabIndex={0}
              >
                {alert.name}
              </span>
              <span className="building-home-alert-info">{`Alert Tier: ${severity}`}</span>
              <span className="building-home-alert-info">
                {`${moment(alert.last_seen_timestamp).fromNow()} • ${moment(alert.last_seen_timestamp).format(
                  'MMM D, YYYY (LT)',
                )}`}
              </span>
              <div className="building-home-alert-buttons">
                <Button
                  className="building-home-alert-view-button"
                  onClick={() => history.push(`/alerts/${building}?incident_id=${incidentId}&event_id=${eventId}`)}
                >
                  View Alert
                </Button>
                <div className="building-home-alert-snooze-container">
                  {alert.snooze_context.is_snoozed ? (
                    <Button
                      className={`snooze-component snooze-component-snoozed snooze-component-${theme}`}
                      onClick={() => onSnoozeButtonPress(alert)}
                    >
                      Unsnooze
                    </Button>
                  ) : (
                    <>
                      <Button
                        className={`snooze-component snooze-component-unsnoozed snooze-component-${theme}`}
                        onClick={() => onSnoozeButtonPress(alert)}
                      >
                        <img src={snoozeIconBlue} alt="snooze" />
                      </Button>
                      <Dropdown overlay={snoozeMenu} trigger={['click']} onClick={() => setSnoozedAlert(alert)}>
                        <Button className={`dropdown-component dropdown-component-${theme}`}>
                          <img src={snoozeDropdownArrow} alt="dropdown" />
                        </Button>
                      </Dropdown>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className={`building-home building-home-${theme}`}>
      <div className="building-home-header">
        <div className="building-name">{buildingName}</div>
        <div className="building-greeting">
          {`${greeting} ${firstName}, here's what's happening with your building today`}
        </div>
      </div>
      <div className="building-home-main-content">
        {permissions.indexOf('savings') !== -1 && (
          <div className="building-home-main-savings-section">
            <div className="building-home-main-savings-row">
              <div className="building-home-main-savings__card">
                {isLoading ? (
                  <div className="building-home--loading">
                    <Spin />
                  </div>
                ) : (
                  <MonthlyCard
                    monthlyData={monthlyData}
                    monthlyDates={monthlyDates}
                    selectedMonthlyDate={selectedMonthlyDate}
                    updateMonthlyDate={updateMonthlyDate}
                    theme={theme}
                  />
                )}
              </div>
              <div className="building-home-main-savings__card">
                {isLoading ? (
                  <div className="building-home--loading">
                    <Spin />
                  </div>
                ) : (
                  <>
                    <div className={`monthly-card monthly-card-${theme}`}>
                      <div className="monthly-card--header">
                        <div className="monthly-card--title">Annual Period</div>
                      </div>
                      <div className="monthly-savings-waiting">
                        <img src={waitingIcon} alt="annual waiting" className="modal-savings-waiting" />
                        <div className="monthly-savings-text-main">We are collecting data right now</div>
                        <div className="monthly-savings-text-secondary">
                          Your savings will appear after software implementation period is complete
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="building-home-main-savings-row">
              <div className="building-home-main-savings__card">
                {isLoading ? (
                  <div className="building-home--loading">
                    <Spin />
                  </div>
                ) : (
                  <>
                    <div className={`monthly-card monthly-card-${theme}`}>
                      <div className="monthly-card--header">
                        <div className="monthly-card--title">Cumulative Utility Period</div>
                      </div>
                      <div className="monthly-savings-waiting">
                        <img
                          src={waitingOnIcon}
                          alt="cumulative waiting"
                          className="modal-savings-waiting-cumulative"
                        />
                        <div className="monthly-savings-text-main">We are collecting data right now</div>
                        <div className="monthly-savings-text-secondary">
                          Your savings will appear after software implementation period is complete
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="building-home-main-savings__card">
                {isLoading ? (
                  <div className="building-home--loading">
                    <Spin />
                  </div>
                ) : (
                  <div className="building-home-main-savings__card--environmental">
                    <div className="building-home-main-savings__card--header">
                      <div className="building-home-main-savings__card--title">Environmental Impact</div>
                    </div>
                    <div className="building-home-main-savings__card--body">
                      <div className="savings__card-data">
                        <div className="savings__card-data-group">
                          <div className="savings__card-data-main">
                            {`${''.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} lbs`}
                          </div>
                          <div className="savings__card-data-secondary">CO₂ Emissions Prevented</div>
                        </div>
                        <div className="savings__card-data-group">
                          <div className="savings__card-data-main">
                            {''.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          </div>
                          <div className="savings__card-data-secondary">Trees Planted (Equivalent)</div>
                        </div>
                      </div>
                    </div>
                    {theme === 'light' ? (
                      <img
                        className="environmental-impact_img"
                        src={environmentalImpactIconLight}
                        alt="environmental-impact"
                      />
                    ) : (
                      <img
                        className="environmental-impact_img"
                        src={environmentalImpactIconDark}
                        alt="environmental-impact"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        <div className="building-home-main-alerts-card">
          {isLoading ? (
            <div className="building-home-alerts-loading">
              <Spin />
            </div>
          ) : (
            <>
              {alertsSetup ? (
                <>
                  <div className="building-home-alerts-header">
                    <div className="building-home-alerts-title">{`Current Alerts (${activeAlerts.length})`}</div>
                    <div className="building-home-alerts-toggle-category">
                      <Radio.Group onChange={(e) => setAlertsCategory(e.target.value)} defaultValue="New">
                        <Radio.Button className="building-home-alerts-new-button" value="New">
                          {`New (${nonSnoozedAlerts.length})`}
                        </Radio.Button>
                        <Radio.Button className="building-home-alerts-snoozed-button" value="Snoozed">
                          {`Snoozed (${snoozedAlerts.length})`}
                        </Radio.Button>
                      </Radio.Group>
                    </div>
                  </div>
                  {activeAlerts.length > 0 ? (
                    <ConfigProvider
                      renderEmpty={alertsCategory === 'New' ? renderNoNonSnoozedAlerts : renderNoSnoozedAlerts}
                    >
                      <Table
                        className="building-home-alerts-list"
                        columns={activeAlertsTableColumns}
                        dataSource={alertsCategory === 'New' ? [...nonSnoozedAlerts] : [...snoozedAlerts]}
                        pagination={paginationProperties}
                      />
                    </ConfigProvider>
                  ) : (
                    <div className="building-home-alerts-no-alerts">
                      <img className="no-alerts-image" src={noAlerts} alt="no-alerts" />
                      <div className="building-home-alerts-heading">
                        Good news! Nothing needs your attention right now
                      </div>
                      <div className="building-home-alerts-text">
                        We will alert you when there are problems with your building
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="building-home-alerts-header">
                    <div className="building-home-alerts-title">New Alerts (0)</div>
                  </div>
                  <div className="building-home-alerts-no-alerts">
                    {theme === 'light' ? (
                      <img className="alerts-not-setup-image" src={alertsNotSetupLight} alt="alerts-not-setup" />
                    ) : (
                      <img className="alerts-not-setup-image" src={alertsNotSetupDark} alt="alerts-not-setup" />
                    )}

                    <div className="building-home-alerts-heading">
                      You will start getting notified of alerts once your building is fully set up
                    </div>
                    <div className="building-home-alerts-text">
                      Current alerts for this building may not be displayed
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
      {isCustomSnoozeModalOpen && (
        <CustomSnoozeModal
          onCancel={() => setIsCustomSnoozeModalOpen(false)}
          onSubmit={submitSnoozeAlert}
          theme={theme}
        />
      )}
    </div>
  );
};
